import axios, { AxiosResponse, AxiosError } from 'axios';

// Base API configuration
export const api = axios.create({
	baseURL: '/api',
	timeout: 15000,
	withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
	(config) => {
		if (config.headers) {
			// Only set Content-Type to JSON if not sending FormData
			if (!(config.data instanceof FormData)) {
				config.headers['Content-Type'] = 'application/json';
			}

			// Add screen size information
			config.headers['X-Screen-Width'] = window.screen.width.toString();
			config.headers['X-Screen-Height'] = window.screen.height.toString();
			config.headers['X-Viewport-Width'] = window.innerWidth.toString();
			config.headers['X-Viewport-Height'] = window.innerHeight.toString();
			config.headers['X-Device-Pixel-Ratio'] = window.devicePixelRatio.toString();
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// Response interceptor
api.interceptors.response.use(
	(response: AxiosResponse) => {
		return response;
	},
	(error: AxiosError<ApiError>) => {
		if (error.response?.status === 401) {
			// Only redirect when the backend explicitly signals an auth failure.
			// Avoid redirecting on accidental 401s from unrelated middleware errors.
			const errMsg = error.response.data?.error ?? error.response.data?.message ?? '';
			const isAuthFailure =
				typeof errMsg === 'string' &&
				(/unauthorized|login to access|authentication failed/i.test(errMsg));

			if (isAuthFailure) {
				window.location.href = '/login';
			}
		}

		return Promise.reject(error);
	}
);

// Canonical error response shape from backend
export interface ApiError {
	success: false;
	message: string;
	error?: string;
	/** Application-specific error code (string, not an HTTP status). */
	code?: string;
	errors?: Array<{ field: string; message: string }>;
}

// Unified API Client with comprehensive methods
class ApiClient {
	private instance = api;

	// Standard HTTP methods
	async get<T>(url: string, params?: Record<string, unknown>): Promise<{ data: T }> {
		const response = await this.instance.get(url, { params });
		return { data: response.data };
	}

	async post<T>(url: string, data?: unknown): Promise<{ data: T }> {
		const response = await this.instance.post(url, data);
		return { data: response.data };
	}

	async put<T>(url: string, data?: unknown): Promise<{ data: T }> {
		const response = await this.instance.put(url, data);
		return { data: response.data };
	}

	async patch<T>(url: string, data?: unknown): Promise<{ data: T }> {
		const response = await this.instance.patch(url, data);
		return { data: response.data };
	}

	async delete<T>(url: string): Promise<{ data: T }> {
		const response = await this.instance.delete(url);
		return { data: response.data };
	}

	/**
	 * Blob download method for exports. `onProgress` nhận số byte đã tải —
	 * server không gửi `Content-Length` (chunked transfer), nên không biết
	 * tổng số byte trước; chỉ báo được tiến độ theo byte đã nhận, không phải
	 * phần trăm.
	 */
	async downloadBlob(
		url: string,
		params?: Record<string, unknown> | URLSearchParams,
		filename?: string,
		onProgress?: (bytesReceived: number) => void
	): Promise<void> {
		try {
			const response = await this.instance.get(url, {
				params,
				responseType: 'blob',
				// Exports stream from the server, so a large dataset can
				// legitimately take minutes. Sits just above nginx's 300s
				// proxy_read_timeout on /api/admin/export/ so a server-side
				// failure surfaces as a real 504 rather than being masked by
				// the client aborting first. Never unbounded: a mid-stream
				// backend stall must eventually surface as an error instead of
				// spinning forever.
				timeout: 330000,
				onDownloadProgress: onProgress ? (event) => onProgress(event.loaded) : undefined,
			});

			// Extract filename from response headers if not provided
			const contentDisposition = response.headers['content-disposition'];
			const defaultFilename = filename || this.extractFilenameFromHeader(contentDisposition) || 'tai-xuong.xlsx';

			// Create download link
			const blob = new Blob([response.data]);
			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.download = defaultFilename;

			// Trigger download
			document.body.appendChild(link);
			link.click();

			// Cleanup
			document.body.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			// With responseType 'blob', an error response body is a Blob too, so
			// the server's JSON message (e.g. the 429 "an export is already
			// running" notice) would otherwise never reach the user — they'd just
			// see a generic download failure. Decode it back into a normal
			// AxiosError shape so the caller's error handling works as usual.
			await this.decodeBlobErrorBody(error);
			console.error('Download failed:', error);
			throw error;
		}
	}

	/** Rewrites an AxiosError whose `response.data` is a Blob into parsed JSON, in place. */
	private async decodeBlobErrorBody(error: unknown): Promise<void> {
		const response = (error as AxiosError)?.response;
		if (!(response?.data instanceof Blob)) return;

		try {
			const text = await response.data.text();
			response.data = JSON.parse(text);
		} catch {
			// Not JSON (an HTML 504 page from nginx, say) — leave the body alone
			// rather than masking the original error with a parse failure.
		}
	}

	// Export method - accepts URLSearchParams or plain record
	async exportToExcel(
		endpoint: string,
		params?: URLSearchParams | Record<string, string | number>,
	): Promise<void> {
		// URLSearchParams đi thẳng vào axios: Object.fromEntries sẽ làm rơi giá trị
		// lặp của tham số mảng (maTinh=01&maTinh=02 chỉ còn 02).
		const query = params instanceof URLSearchParams || params === undefined
			? params
			: Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]));
		return this.downloadBlob(endpoint, query);
	}

	// Extract filename from Content-Disposition header (RFC 5987 + legacy)
	private extractFilenameFromHeader(contentDisposition?: string): string | null {
		if (!contentDisposition) return null;

		// RFC 5987: filename*=UTF-8''encoded-name
		const rfc5987Match = contentDisposition.match(/filename\*=(?:UTF-8|utf-8)''(.+?)(?:;|$)/);
		if (rfc5987Match?.[1]) {
			return decodeURIComponent(rfc5987Match[1].trim());
		}

		// Legacy: filename="name" or filename=name
		const legacyMatch = contentDisposition.match(/filename=(["']?)(.+?)\1(?:;|$)/);
		if (legacyMatch?.[2]) {
			return legacyMatch[2].trim();
		}

		return null;
	}

	// Raw axios instance for complex requests
	get raw() {
		return this.instance;
	}
}

// Export unified client instance
export const apiClient = new ApiClient();